/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Button, theme } from "@nordcloud/gnui";
import { useDisclosure } from "~/hooks";
import { KeyValue } from "~/models";
import { isNotEmpty } from "~/tools";
import { AddResourceGroupForm, AddTagForm } from "../components";
import { RuleType } from "../types";
import { AwsSecurityGroupSelector } from "./AwsSecurityGroupSelector/AwsSecurityGroupSelector";
import { CloudAccountSelector } from "./CloudAccountSelector";
import { GcpProjectSelector } from "./GcpProjectSelector";
import { ResourceTypeSelector } from "./ResourceTypeSelector";
import { VpcSubnetSelector } from "./VpcSubnetSelector";

type Props = {
  selector: RuleType;
  onSubmit: (item: KeyValue | string) => void;
  label: string;
  excludeSelectorItems?: string[];
  onDelete: (ruleType: RuleType, rule?: KeyValue | string) => void;
};

export function AddAdditionalRule({
  selector,
  onSubmit,
  label,
  excludeSelectorItems = [],
  onDelete,
}: Props) {
  const { isOpen, close, open } = useDisclosure();

  const handleSubmit = (item: KeyValue | string) => {
    onSubmit(item);
    close();
  };

  const handleMultiSubmit = (selected: string[], removed: string[]) => {
    selected.forEach((item) => {
      if (!excludeSelectorItems.includes(item)) {
        onSubmit(item);
      }
    });

    if (selector === RuleType.SECURITY_GROUP_IDS && isNotEmpty(removed)) {
      removed.forEach((item) => onDelete(RuleType.SECURITY_GROUP_IDS, item));
    }

    close();
  };

  const deleteSubnets = (subnetIds: string[]) => {
    if (selector === RuleType.AWS_VPC) {
      subnetIds.forEach((subnetId) => onDelete(selector, subnetId));
    }
  };

  const isTagFormActive = selector === RuleType.RESOURCE_TAGS;
  const isResourceGroupFormActive = selector === RuleType.RESOURCE_GROUP;
  const isIbmResourceGroupFormActive = selector === RuleType.IBM_RESOURCE_GROUP;
  const isGcpProjectSelectorActive = selector === RuleType.PROJECT_ID;
  const isResourceTypeSelectorActive = selector === RuleType.RESOURCE_TYPE;
  const isCloudAccountSelectorActive = selector === RuleType.CLOUD_ACCOUNT;
  const isVpcSubnetSelectorActive = selector === RuleType.AWS_VPC;
  const isSecurityGroupSelectorActive =
    selector === RuleType.SECURITY_GROUP_IDS;

  return (
    <div>
      <Button
        icon="plus"
        severity="low"
        type="button"
        size="md"
        mt={theme.spacing.spacing01}
        onClick={open}
      >
        Add {label}
      </Button>
      <AddTagForm
        isOpen={isOpen && isTagFormActive}
        onSubmit={handleSubmit}
        onClose={close}
      />
      <AddResourceGroupForm
        isIbmResourceGroup={isIbmResourceGroupFormActive}
        isOpen={
          isOpen && (isResourceGroupFormActive || isIbmResourceGroupFormActive)
        }
        onSubmit={handleSubmit}
        onClose={close}
      />
      <GcpProjectSelector
        isOpen={isOpen && isGcpProjectSelectorActive}
        excludeItems={excludeSelectorItems}
        onSubmit={handleSubmit}
        onClose={close}
      />
      <ResourceTypeSelector
        isOpen={isOpen && isResourceTypeSelectorActive}
        excludeItems={excludeSelectorItems}
        onSubmit={handleSubmit}
        onClose={close}
      />
      <CloudAccountSelector
        isOpen={isOpen && isCloudAccountSelectorActive}
        excludeItems={excludeSelectorItems}
        onSubmit={handleSubmit}
        onClose={close}
      />
      <AwsSecurityGroupSelector
        isOpen={isOpen && isSecurityGroupSelectorActive}
        excludeItems={excludeSelectorItems}
        onSubmit={handleMultiSubmit}
        onClose={close}
      />
      <VpcSubnetSelector
        handleDelete={deleteSubnets}
        isOpen={isOpen && isVpcSubnetSelectorActive}
        excludeItems={excludeSelectorItems}
        onSubmit={handleSubmit}
        onClose={close}
      />
    </div>
  );
}
